import React, { useState } from 'react';
import axios from 'axios';
import {
  useScheduleProfiles,
  useCreateScheduleProfile,
  useUpdateScheduleProfile,
  useDeleteScheduleProfile,
} from '../hooks/queries/useAdmin';
import { BackoffStage, ScheduleProfile } from '../api/admin';

const MULTIPLIER_FIELDS: Array<{ key: keyof ScheduleProfile; label: string }> = [
  { key: 'multiplierWeekdayDay', label: 'Wd Day' },
  { key: 'multiplierWeekdayEvening', label: 'Wd Evening' },
  { key: 'multiplierWeekdayNight', label: 'Wd Night' },
  { key: 'multiplierWeekendDay', label: 'We Day' },
  { key: 'multiplierWeekendEvening', label: 'We Evening' },
  { key: 'multiplierWeekendNight', label: 'We Night' },
];

type FormState = {
  name: string;
  multiplierWeekdayDay: string;
  multiplierWeekdayEvening: string;
  multiplierWeekdayNight: string;
  multiplierWeekendDay: string;
  multiplierWeekendEvening: string;
  multiplierWeekendNight: string;
  deactivationThreshold: string;
  backoffCurve: BackoffStage[];
};

const emptyForm = (): FormState => ({
  name: '',
  multiplierWeekdayDay: '1',
  multiplierWeekdayEvening: '1',
  multiplierWeekdayNight: '1',
  multiplierWeekendDay: '1',
  multiplierWeekendEvening: '1',
  multiplierWeekendNight: '1',
  deactivationThreshold: '18',
  backoffCurve: [],
});

const profileToForm = (p: ScheduleProfile): FormState => ({
  name: p.name,
  multiplierWeekdayDay: String(p.multiplierWeekdayDay),
  multiplierWeekdayEvening: String(p.multiplierWeekdayEvening),
  multiplierWeekdayNight: String(p.multiplierWeekdayNight),
  multiplierWeekendDay: String(p.multiplierWeekendDay),
  multiplierWeekendEvening: String(p.multiplierWeekendEvening),
  multiplierWeekendNight: String(p.multiplierWeekendNight),
  deactivationThreshold: String(p.deactivationThreshold),
  backoffCurve: p.backoffCurve,
});

function isValidCurve(curve: BackoffStage[]): boolean {
  if (curve.length === 0) return false;
  for (let i = 1; i < curve.length; i++) {
    if (curve[i].errorCount <= curve[i - 1].errorCount) return false;
    if (curve[i].delayMinutes < curve[i - 1].delayMinutes) return false;
  }
  return curve.every((s) => Number.isFinite(s.errorCount) && s.errorCount >= 1 && Number.isFinite(s.delayMinutes) && s.delayMinutes >= 0);
}

function getProfileLoadError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error;
    return `${error.response?.status ?? 'Netzwerkfehler'}: ${typeof message === 'string' ? message : error.message}`;
  }
  return error instanceof Error ? error.message : 'Unbekannter Fehler';
}

export const ScheduleProfilesPanel: React.FC = () => {
  const { data: profiles = [], isLoading, error, refetch } = useScheduleProfiles();
  const createProfile = useCreateScheduleProfile();
  const updateProfile = useUpdateScheduleProfile();
  const deleteProfile = useDeleteScheduleProfile();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [conflict, setConflict] = useState<string[] | null>(null);

  const startCreate = () => {
    setEditingId('__new__');
    setForm(emptyForm());
    setConflict(null);
  };

  const startEdit = (p: ScheduleProfile) => {
    setEditingId(p.id);
    setForm(profileToForm(p));
    setConflict(null);
  };

  const cancel = () => {
    setEditingId(null);
    setForm(null);
  };

  const save = async () => {
    if (!form) return;
    if (!form.name.trim()) { alert('Name ist erforderlich'); return; }
    if (!isValidCurve(form.backoffCurve)) { alert('Backoff-Kurve muss streng steigende errorCount und nicht-fallende delayMinutes haben'); return; }
    const payload = {
      name: form.name.trim(),
      multiplierWeekdayDay: Number(form.multiplierWeekdayDay),
      multiplierWeekdayEvening: Number(form.multiplierWeekdayEvening),
      multiplierWeekdayNight: Number(form.multiplierWeekdayNight),
      multiplierWeekendDay: Number(form.multiplierWeekendDay),
      multiplierWeekendEvening: Number(form.multiplierWeekendEvening),
      multiplierWeekendNight: Number(form.multiplierWeekendNight),
      deactivationThreshold: Number(form.deactivationThreshold),
      backoffCurve: form.backoffCurve,
    };
    try {
      if (editingId === '__new__') {
        await createProfile.mutateAsync(payload);
      } else if (editingId) {
        await updateProfile.mutateAsync({ id: editingId, payload });
      }
      cancel();
    } catch (err: any) {
      alert(`Fehler: ${err?.response?.data?.error || err.message}`);
    }
  };

  const remove = async (id: string) => {
    setConflict(null);
    try {
      await deleteProfile.mutateAsync(id);
    } catch (err: any) {
      const refs = err?.response?.data?.referencingScraperKeys;
      if (refs) {
        setConflict(refs);
      } else {
        alert(`Fehler: ${err?.response?.data?.error || err.message}`);
      }
    }
  };

  return (
    <div className="admin-system-card">
      <h4 style={{ margin: '0 0 16px 0', color: 'var(--text-main)' }}>Schedule Profiles</h4>
      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        Profile definieren Intervall-Multiplikatoren pro Wochentag/Wochenende × Schicht und die Backoff-Kurve bei Fehlern.
      </p>

      {conflict && (
        <div style={{ marginBottom: '12px', fontSize: '12px', color: 'rgb(var(--c-danger-text))' }}>
          Kann nicht gelöscht werden - noch verwendet von: {conflict.join(', ')}
          <button onClick={() => setConflict(null)} className="admin-btn-utility" style={{ marginLeft: '8px', fontSize: '11px' }}>OK</button>
        </div>
      )}

      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Lade...</div>
      ) : error ? (
        <div role="alert" style={{ color: 'rgb(var(--c-danger-text))', fontSize: '13px', border: '1px solid rgb(var(--c-danger-text))', borderRadius: '6px', padding: '10px' }}>
          <strong>Profile konnten nicht geladen werden.</strong><br />
          {getProfileLoadError(error)}
          <button onClick={() => refetch()} className="admin-btn-utility" style={{ display: 'block', marginTop: '8px' }}>
            Erneut versuchen
          </button>
        </div>
      ) : (
        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px', marginBottom: '12px' }}>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Multiplikatoren</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Deaktivierung</th>
                <th style={{ padding: '6px 8px', width: '90px' }}></th>
              </tr>
            </thead>
            <tbody>
              {profiles.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '10px 8px', color: 'var(--text-muted)' }}>Keine Profile vorhanden.</td>
                </tr>
              )}
              {profiles.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '6px 8px' }}>{p.name}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    {MULTIPLIER_FIELDS.map((f) => `${(p[f.key] as number).toFixed(1)}`).join(' / ')}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.deactivationThreshold}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <button onClick={() => startEdit(p)} className="admin-btn-utility" style={{ fontSize: '11px', marginRight: '4px' }}>✏️</button>
                    <button onClick={() => remove(p.id)} className="admin-btn-utility" style={{ fontSize: '11px' }}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!form ? (
        <button onClick={startCreate} className="btn admin-rebuild-btn-blue" style={{ fontSize: '13px', padding: '10px 16px' }}>
          + Neues Profil
        </button>
      ) : (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '12px' }}>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Name</label>
            <input className="admin-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ fontSize: '12px', padding: '4px 6px' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
            {MULTIPLIER_FIELDS.map((f) => (
              <div key={String(f.key)}>
                <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>{f.label}</label>
                <input
                  type="number"
                  step="0.1"
                  className="admin-input"
                  value={(form as any)[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value } as FormState)}
                  style={{ fontSize: '12px', padding: '4px 6px' }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Deaktivierungs-Schwelle (Fehleranzahl)</label>
            <input
              type="number"
              className="admin-input"
              value={form.deactivationThreshold}
              onChange={(e) => setForm({ ...form, deactivationThreshold: e.target.value })}
              style={{ fontSize: '12px', padding: '4px 6px', width: '120px' }}
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Backoff-Kurve (errorCount → delayMinutes)</label>
            {form.backoffCurve.map((stage, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                <input
                  type="number"
                  className="admin-input"
                  value={stage.errorCount}
                  onChange={(e) => {
                    const curve = [...form.backoffCurve];
                    curve[idx] = { ...curve[idx], errorCount: Number(e.target.value) };
                    setForm({ ...form, backoffCurve: curve });
                  }}
                  style={{ fontSize: '12px', padding: '4px 6px', width: '80px' }}
                />
                <input
                  type="number"
                  className="admin-input"
                  value={stage.delayMinutes}
                  onChange={(e) => {
                    const curve = [...form.backoffCurve];
                    curve[idx] = { ...curve[idx], delayMinutes: Number(e.target.value) };
                    setForm({ ...form, backoffCurve: curve });
                  }}
                  style={{ fontSize: '12px', padding: '4px 6px', width: '80px' }}
                />
                <button
                  onClick={() => setForm({ ...form, backoffCurve: form.backoffCurve.filter((_, i) => i !== idx) })}
                  className="admin-btn-utility"
                  style={{ fontSize: '11px' }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              onClick={() => setForm({ ...form, backoffCurve: [...form.backoffCurve, { errorCount: 0, delayMinutes: 0 }] })}
              className="admin-btn-utility"
              style={{ fontSize: '11px' }}
            >
              + Stufe hinzufügen
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={save} className="btn admin-rebuild-btn-blue" style={{ fontSize: '12px', padding: '6px 14px' }}>Speichern</button>
            <button onClick={cancel} className="admin-btn-utility">Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
};
